import React from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Home,
  Dashboard,
  Assessment,
  Receipt,
  Visibility,
  Article,
  MonetizationOn,
  ChevronLeft,
  ChevronRight,
  LightMode,
  DarkMode,
  Logout,
  HelpOutline,
  SmartToy,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';

const SIDEBAR_WIDTH = 240;
const SIDEBAR_COLLAPSED_WIDTH = 64;

const navItems = [
  { text: 'Home', icon: <Home />, path: '/home' },
  { text: 'Portfolio', icon: <Dashboard />, path: '/portfolio' },
  { text: 'Research', icon: <Assessment />, path: '/research' },
  { text: 'Orders', icon: <Receipt />, path: '/orders' },
  { text: 'Watchlist', icon: <Visibility />, path: '/watchlist' },
  { text: 'News', icon: <Article />, path: '/news' },
  { text: 'P&L', icon: <MonetizationOn />, path: '/pnl' },
  { text: 'Help', icon: <HelpOutline />, path: '/help' },
  { text: 'Assistant', icon: <SmartToy />, path: '/assistant', matchPrefix: true },
];

const Sidebar = ({ collapsed, onToggleCollapse, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();

  const width = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: collapsed ? 1.5 : 2,
          py: 2.5,
          borderBottom: 1,
          borderColor: 'divider',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #58a6ff, #3fb950)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: 13,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          SA
        </Box>
        {!collapsed && (
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 15 }}>
            Simple Analysis
          </Typography>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 1, py: 1.5 }}>
        {navItems.map((item) => {
          const isActive = item.matchPrefix
            ? location.pathname === item.path || location.pathname.startsWith(item.path + '/')
            : location.pathname === item.path;
          return (
            <Tooltip
              key={item.text}
              title={collapsed ? item.text : ''}
              placement="right"
              arrow
            >
              <ListItem disablePadding sx={{ mb: 0.3 }}>
                <ListItemButton
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    borderRadius: 2,
                    px: collapsed ? 1.5 : 1.5,
                    py: 1,
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? '#fff' : 'text.secondary',
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.main' : 'action.hover',
                      color: isActive ? '#fff' : 'text.primary',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 36,
                      color: 'inherit',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5 }}>
        {/* Collapse toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <IconButton size="small" onClick={onToggleCollapse} sx={{ color: 'text.secondary' }}>
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Box>

        {/* Theme toggle */}
        <Tooltip title={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} placement="right">
          <ListItemButton
            onClick={toggleTheme}
            sx={{
              borderRadius: 2,
              py: 0.8,
              px: collapsed ? 1.5 : 1.5,
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: 'text.secondary' }}>
              {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                primaryTypographyProps={{ fontSize: 13, color: 'text.secondary' }}
              />
            )}
          </ListItemButton>
        </Tooltip>

        <Divider sx={{ my: 1 }} />

        {/* User profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: collapsed ? 0 : 0.5,
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          <Avatar
            src={user?.profilePicture}
            sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}
          >
            {user?.name?.charAt(0) || 'U'}
          </Avatar>
          {!collapsed && (
            <Box sx={{ overflow: 'hidden', flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }} noWrap>
                {user?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 11 }} noWrap>
                {user?.email || ''}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Logout */}
        <Tooltip title={collapsed ? 'Logout' : ''} placement="right">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 0.8,
              px: collapsed ? 1.5 : 1.5,
              mt: 1,
              justifyContent: collapsed ? 'center' : 'flex-start',
              color: 'error.main',
              '&:hover': { backgroundColor: 'error.main', color: '#fff' },
            }}
          >
            <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: 'inherit' }}>
              <Logout fontSize="small" />
            </ListItemIcon>
            {!collapsed && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontSize: 13 }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop: permanent drawer */}
      <Drawer
        variant="permanent"
        aria-label="Main navigation"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width,
            transition: 'width 0.2s ease',
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Mobile: temporary drawer */}
      <Drawer
        variant="temporary"
        aria-label="Main navigation"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default Sidebar;
